/**
 * Context Binding Engine — declarative binding registry.
 *
 * `CONTEXT_BINDING_REGISTRY` is the single, code-checked source of truth
 * for how an extraction tag maps onto one-or-many target fields. It is the
 * CBE successor to `SED_BINDING_REGISTRY` (sedBindingRegistry.ts, 18 entries
 * spanning 19 tag→path pairs — `technology.reactor` fans onto two paths):
 *
 *   - The first block MIGRATES all 19 SED tag→path pairs 1:1 (same tag →
 *     same SED path) so the SED-send + bi-dir-sync behaviour round-trips. A
 *     registry-round-trip test pins this 1:1 correspondence over ALL paths.
 *   - The second block ADDS the net-new one-tag-many-fields rows the
 *     SED-only registry could not express (parcel.acres fan-out via locus
 *     keyword, schedule.inservice NTP-vs-COD, finance cost / ppa, water,
 *     power) — these unlock the ~37 financial + intent fields the CBE owns.
 *
 * Authoring contract:
 *   - PURE static `const`. Adding a binding is a code change + a test.
 *   - One tag MAY have MANY rows (fan-out); the `factMatcher` disambiguates.
 *   - `valueKind` MUST match the coercion's output kind (pinned by test).
 *   - `fillPolicy.ownership` follows the source-ownership model: Context
 *     OWNS the ~37 net-new fields, COMPLEMENTS BOM (capex) + Intent
 *     (capacity / inServiceDate / ppa price), and is NOT writable for
 *     provider-owned SED facts (those cross-check only).
 *
 * Precedence ranks (lower = higher authority, wins / fills first):
 *   - 20  finance / capex (complements BOM; BOM is the lower-rank owner).
 *   - 30  parcel acreage SED facts.
 *   - 40  schedule / water / power / technology / permitting intent facts.
 *   - 50  finance.ppa / commitment incentives (Context is the sole owner).
 *
 * WIRING STATUS: all four callers are LIVE.
 *   1. Financial pull  — contextFinancialFill.ts / pullFinancialFromSources store action.
 *   2. SED-send        — buildSiteContextBlock (reportPrompts.ts), CBE-A2 markers.
 *   3. Bi-dir sync     — syncSedContextBindings.ts / sedAmendments store path.
 *   4. Intent fill     — contextIntentFill.ts / the store claim-merge paths (gap-fill only).
 */
import type { CbeBinding } from './cbeTypes';

/* ── Locus keyword vocabularies (reused by locusKeyword matchers) ─────── */

/** "this acreage is the BUILDABLE / developable subset". */
const BUILDABLE_KEYWORDS = ['buildable', 'developable', 'usable', 'net usable'] as const;
/** "this acreage is the DATA-CENTER parcel". */
const DATACENTER_KEYWORDS = ['data center', 'datacenter', 'campus', 'it hall'] as const;
/** "this acreage is the SUBSTATION / switchyard parcel". */
const SUBSTATION_KEYWORDS = ['substation', 'switchyard', 'switching station'] as const;
/** "this acreage is the EXPANSION / future-phase parcel". */
const EXPANSION_KEYWORDS = ['expansion', 'phase 2', 'phase ii', 'future', 'reserved'] as const;
/** "this date is COMMERCIAL-OPERATION-DATE / in-service". */
const COD_KEYWORDS = ['cod', 'commercial operation', 'in service', 'in-service', 'energization', 'energized'] as const;

/* ── 1:1 migration of the 19 SED_BINDING_REGISTRY tag→path pairs ──────── */
//
// Each row preserves the original tag → SED path (one row per pair; the
// legacy `technology.reactor` entry contributes two rows). Coercions mirror
// the original identity projections (identityNumber/String/Boolean). All are
// `factMatcher: always` because the SED registry keyed on TAG only (no
// fan-out). These round-trip under the registry-round-trip test.

const MIGRATED_SED_BINDINGS: readonly CbeBinding[] = [
  {
    sourceTag: 'parcel.acres',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'parcel.acres' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 30, refreshable: true },
    label: 'Site area (acres)',
  },
  {
    sourceTag: 'power.grid',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'grid.substationDistKm' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Distance to substation (km)',
  },
  {
    sourceTag: 'connectivity.fiber',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'connectivity.nearestIxDistKm' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Distance to nearest internet exchange (km)',
  },
  {
    sourceTag: 'environmental.wetlands',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'environmental.hasWetlands' },
    coercion: 'toBool',
    valueKind: 'bool',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Wetlands present on parcel',
  },
  {
    sourceTag: 'regulatory.zoning',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'parcel.zoning' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Parcel zoning',
  },
  {
    sourceTag: 'geotechnical.seismic',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'seismic.designCategory' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Seismic design category',
  },
  {
    sourceTag: 'schedule.inservice',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'project.inServiceDate' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'In-service date',
  },
  {
    sourceTag: 'parcel.boundaries',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'parcel.developableAcres' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 30, refreshable: true },
    label: 'Developable acres',
  },
  {
    sourceTag: 'water.rights',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'water.rightsStatus' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Water rights status',
  },
  {
    sourceTag: 'water.source',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'water.stressLevel' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Water stress level',
  },
  {
    sourceTag: 'environmental.species',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'environmental.speciesConcerns' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Species concerns',
  },
  {
    sourceTag: 'environmental.nepa',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'regulatory.nepaPathway' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'NEPA pathway',
  },
  {
    sourceTag: 'environmental.tribal',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'regulatory.tribalLandProximity' },
    coercion: 'toBool',
    valueKind: 'bool',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Tribal land proximity',
  },
  {
    sourceTag: 'connectivity.latency',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'connectivity.latencyMs' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Latency to nearest hyperscaler (ms)',
  },
  {
    sourceTag: 'technology.reactor',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'technology.data.reactorDesignLabel' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'SMR reactor candidate',
  },
  {
    // The legacy `technology.reactor` entry fanned ONE tag onto TWO SED
    // paths (reactorDesignLabel + reactorDesignClass). The CBE expresses
    // that as two rows; this second row was dropped in the original
    // migration (caught by the all-sedPaths round-trip test, CBE-A3).
    sourceTag: 'technology.reactor',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'technology.data.reactorDesignClass' },
    coercion: 'toString',
    valueKind: 'complex',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'SMR reactor candidate',
  },
  {
    sourceTag: 'power.btm',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'power.btmCapacityMW' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'BTM generation capacity (MW)',
  },
  {
    sourceTag: 'power.timeline',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'power.timeToPowerYears' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Time-to-power (years)',
  },
  {
    sourceTag: 'finance.cost',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'sed', path: 'financial.capexTotalUsd' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'complements', precedenceRank: 20, refreshable: true },
    label: 'Total CAPEX (USD)',
  },
];

/* ── Net-new CBE rows (one-tag-many-fields fan-out) ───────────────────── */
//
// These are the rows the SED-only registry could not express. They share
// tags with migrated rows but target DIFFERENT surfaces / paths and use
// span-aware factMatchers so they fan a single tag out to many fields.

const NEW_CBE_BINDINGS: readonly CbeBinding[] = [
  // ── parcel.acres × 4 (which-of-many via locus keyword) ──────────────
  // NOTE: a value-position-aware `locusKeyword` (not a whole-span
  // `spanRegex`) so the BUILDABLE figure binds to its own keyword and the
  // county-tract figure in the same span does NOT leak in (the 332-vs-800
  // case). `spanRegex` would fire for either number whenever "buildable"
  // appears anywhere in the span.
  {
    sourceTag: 'parcel.acres',
    factMatcher: { kind: 'locusKeyword', keywords: BUILDABLE_KEYWORDS, window: 80 },
    targetField: { surface: 'sed', path: 'parcel.developableAcres' },
    coercion: 'unitNormalize',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 30, refreshable: true },
    label: 'Buildable / developable acres',
  },
  {
    sourceTag: 'parcel.acres',
    factMatcher: { kind: 'locusKeyword', keywords: DATACENTER_KEYWORDS, window: 80 },
    targetField: { surface: 'sed', path: 'parcel.dataCenterAcres' },
    coercion: 'unitNormalize',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 30, refreshable: true },
    label: 'Data-center parcel acres',
  },
  {
    sourceTag: 'parcel.acres',
    factMatcher: { kind: 'locusKeyword', keywords: SUBSTATION_KEYWORDS, window: 80 },
    targetField: { surface: 'sed', path: 'parcel.substationAcres' },
    coercion: 'unitNormalize',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 30, refreshable: true },
    label: 'Substation parcel acres',
  },
  {
    sourceTag: 'parcel.acres',
    factMatcher: { kind: 'locusKeyword', keywords: EXPANSION_KEYWORDS, window: 80 },
    targetField: { surface: 'sed', path: 'parcel.expansionAcres' },
    coercion: 'unitNormalize',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 30, refreshable: true },
    label: 'Expansion parcel acres',
  },

  // ── schedule.inservice × 4 (NTP / COD / durations) ──────────────────
  {
    sourceTag: 'schedule.inservice',
    factMatcher: { kind: 'locusKeyword', keywords: COD_KEYWORDS, window: 120 },
    targetField: { surface: 'intent', path: 'inServiceDate' },
    coercion: 'toDate',
    valueKind: 'date',
    // Intent OWNS inServiceDate; context COMPLEMENTS (gap-fills a blank).
    fillPolicy: { ownership: 'complements', precedenceRank: 40, refreshable: true },
    label: 'Commercial operation date (COD)',
  },
  {
    sourceTag: 'schedule.inservice',
    factMatcher: { kind: 'locusKeyword', keywords: COD_KEYWORDS, window: 120 },
    targetField: { surface: 'financialPhase', path: 'inServiceDate' },
    coercion: 'toDate',
    valueKind: 'date',
    fillPolicy: { ownership: 'complements', precedenceRank: 40, refreshable: true },
    label: 'Per-phase COD',
  },
  // NOTE: the NTP (notice-to-proceed) date has NO real settable field on
  // `FinancialPhase` (which carries only inServiceDate / constructionMonths)
  // nor on ProjectIntent — so it is intentionally LEFT UNMAPPED per the
  // design's "do not force-fit genuinely unmappable fields" rule. When a
  // dedicated NTP/schedule field lands, add a row here.
  {
    sourceTag: 'schedule.permitting',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'financialPhase', path: 'constructionMonths' },
    coercion: 'toRange',
    valueKind: 'range',
    // Carry {min,max} — NEVER collapse to max (the parseQueueYears bug).
    fillPolicy: { ownership: 'complements', precedenceRank: 40, refreshable: true },
    label: 'Permitting / construction lead (range)',
  },

  // ── finance.cost (CAPEX range / land) ───────────────────────────────
  {
    sourceTag: 'finance.cost',
    factMatcher: { kind: 'spanRegex', re: /capex|capital|cost|build/i },
    targetField: { surface: 'financial', path: 'capexTotalUsd' },
    coercion: 'toRange',
    valueKind: 'range',
    // BOM OWNS capex; context fills only when BOM is absent, divergence
    // surfaces. Lower rank than BOM at merge time.
    fillPolicy: { ownership: 'complements', precedenceRank: 20, refreshable: true },
    label: 'Total CAPEX (range, $)',
  },
  {
    sourceTag: 'finance.cost',
    factMatcher: { kind: 'locusKeyword', keywords: ['land', 'acquisition', 'parcel cost'], window: 80 },
    targetField: { surface: 'financial', path: 'landCost.totalUsd' },
    coercion: 'toRange',
    valueKind: 'range',
    fillPolicy: { ownership: 'complements', precedenceRank: 20, refreshable: true },
    label: 'Land cost ($)',
  },

  // ── finance.ppa (ONE tag → 4 fields) ────────────────────────────────
  {
    sourceTag: 'finance.ppa',
    factMatcher: { kind: 'spanRegex', re: /\$\s*\d|\/\s*mwh|price|tariff/i },
    targetField: { surface: 'financial', path: 'ppaPricePerMwhUsd' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'complements', precedenceRank: 50, refreshable: true },
    label: 'PPA price ($/MWh)',
  },
  {
    sourceTag: 'finance.ppa',
    factMatcher: { kind: 'spanRegex', re: /escalat/i },
    targetField: { surface: 'financial', path: 'ppaEscalatorPctPerYear' },
    coercion: 'toPercent',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 50, refreshable: true },
    label: 'PPA escalator (%/yr)',
  },
  {
    sourceTag: 'finance.ppa',
    factMatcher: { kind: 'spanRegex', re: /\b\d+\s*-?\s*(?:year|yr)\b|term|tenor/i },
    targetField: { surface: 'financial', path: 'ppaTermYears' },
    coercion: 'toYears',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 50, refreshable: true },
    label: 'PPA term (years)',
  },
  {
    sourceTag: 'finance.ppa',
    factMatcher: { kind: 'spanRegex', re: /structure|merchant|contracted|hybrid/i },
    targetField: { surface: 'intent', path: 'offtake.structure' },
    coercion: 'toEnum',
    valueKind: 'enum',
    fillPolicy: { ownership: 'owns', precedenceRank: 50, refreshable: true },
    label: 'Offtake structure',
  },

  // ── water source / cooling ──────────────────────────────────────────
  {
    sourceTag: 'water.source',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'intent', path: 'waterSource.source' },
    coercion: 'toEnum',
    valueKind: 'enum',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Water source (intent enum)',
  },
  {
    sourceTag: 'water.cooling',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'intent', path: 'coolingStrategy' },
    coercion: 'toEnum',
    valueKind: 'enum',
    fillPolicy: { ownership: 'complements', precedenceRank: 40, refreshable: true },
    label: 'Cooling strategy',
  },
  {
    sourceTag: 'water.rights',
    factMatcher: { kind: 'always' },
    targetField: { surface: 'intent', path: 'waterSource.rightsStatus' },
    coercion: 'toEnum',
    valueKind: 'enum',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Water rights status (intent enum)',
  },

  // ── power grid / btm intent ─────────────────────────────────────────
  {
    sourceTag: 'power.btm',
    factMatcher: { kind: 'spanRegex', re: /firm|elcc|capacity credit/i },
    targetField: { surface: 'intent', path: 'capacityCreditIntent.targetMW' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 40, refreshable: true },
    label: 'Firm-capacity intent (MW)',
  },
  {
    sourceTag: 'power.grid',
    factMatcher: { kind: 'spanRegex', re: /queue|interconnection|lgia|load[ -]service/i },
    targetField: { surface: 'intent', path: 'interconnectionStatus.queueStatus' },
    coercion: 'toEnum',
    valueKind: 'enum',
    fillPolicy: { ownership: 'complements', precedenceRank: 40, refreshable: true },
    label: 'Utility service / interconnection status',
  },

  // ── incentives (commitment) ─────────────────────────────────────────
  {
    sourceTag: 'commitment',
    factMatcher: { kind: 'spanRegex', re: /itc|investment tax credit/i },
    targetField: { surface: 'financial', path: 'itcPct' },
    coercion: 'toPercent',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 50, refreshable: true },
    label: 'Investment tax credit (%)',
  },
  {
    sourceTag: 'commitment',
    factMatcher: { kind: 'spanRegex', re: /ptc|production tax credit/i },
    targetField: { surface: 'financial', path: 'ptcPerMwhUsd' },
    coercion: 'toNumber',
    valueKind: 'scalar',
    fillPolicy: { ownership: 'owns', precedenceRank: 50, refreshable: true },
    label: 'Production tax credit ($/MWh)',
  },
];

/** The full CBE binding registry — 19 migrated SED rows + net-new rows. */
export const CONTEXT_BINDING_REGISTRY: readonly CbeBinding[] = [
  ...MIGRATED_SED_BINDINGS,
  ...NEW_CBE_BINDINGS,
];

/** The 19 migrated SED rows in isolation (for the 1:1 round-trip test). */
export const MIGRATED_SED_ROWS: readonly CbeBinding[] = MIGRATED_SED_BINDINGS;

/**
 * O(1) lookup of all binding rows that key on a given tag. Built once at
 * module load. Rows preserve declared order so `pickBestMatcher` sees the
 * stable fallback order.
 */
const BINDINGS_BY_TAG: ReadonlyMap<CbeBinding['sourceTag'], readonly CbeBinding[]> = (() => {
  const m = new Map<CbeBinding['sourceTag'], CbeBinding[]>();
  for (const b of CONTEXT_BINDING_REGISTRY) {
    const list = m.get(b.sourceTag) ?? [];
    list.push(b);
    m.set(b.sourceTag, list);
  }
  return m as ReadonlyMap<CbeBinding['sourceTag'], readonly CbeBinding[]>;
})();

/** Lookup all binding rows that fire for a given extraction tag. */
export function getBindingsForTag(tag: CbeBinding['sourceTag']): readonly CbeBinding[] {
  return BINDINGS_BY_TAG.get(tag) ?? [];
}
